/**
 * The plate-edit warning for a car that carries a live membership (CWS-1384).
 *
 * Changing the plate is not a cosmetic correction on a membership car. The membership
 * is keyed on the vehicle, so it follows the new plate immediately and the lane stops
 * recognising the old one on the very next read. That is usually exactly what the
 * operator wants, and it is never what they expect to happen silently.
 *
 * Informational only: this never blocks the save. Refusing belongs to the delete, which
 * has no safe outcome; an edit does.
 */

/** One active membership as the vehicle endpoint reports it. */
export interface VehicleActiveMembership {
  subscription_id: string;
  status: string;
  package_name?: string | null;
  /** 'order' when the membership's own order points here, 'family_plan' when linked to a plan. */
  link?: string | null;
}

/**
 * Warning text for these memberships, or null when there are none.
 *
 * Names the package when there is exactly one and we know it, because "your Warrior
 * Wash membership" is checkable by the operator in a way that "a membership" is not.
 */
export function activeMembershipPlateWarning(
  memberships: readonly VehicleActiveMembership[] | null | undefined
): string | null {
  const live = (memberships ?? []).filter(Boolean);
  if (live.length === 0) {
    return null;
  }

  const subject = describeSubject(live);
  return `${subject} on this vehicle. Changing the plate moves the membership to the new plate, and the old plate will stop being recognised at the lane.`;
}

/** Whether a plate edit on this vehicle should carry the membership warning. */
export function hasActiveMembershipPlateWarning(
  memberships: readonly VehicleActiveMembership[] | null | undefined
): boolean {
  return activeMembershipPlateWarning(memberships) !== null;
}

function describeSubject(live: readonly VehicleActiveMembership[]): string {
  if (live.length > 1) {
    return `There are ${live.length} active memberships`;
  }
  const name = String(live[0]?.package_name ?? '').trim();
  return name ? `The ${name} membership is active` : 'There is an active membership';
}
